<template>
    <div v-if="loading && !loaded" class="pa-6 text-center">
        <v-progress-circular indeterminate color="primary"/>
    </div>

    <v-alert v-else-if="loadError" type="error" variant="tonal" density="compact">
        {{ loadError }}
        <template #append>
            <v-btn size="small" variant="text" @click="load">Retry</v-btn>
        </template>
    </v-alert>

    <v-row v-else>
        <!-- Sign-in -->
        <v-col cols="12" md="5">
            <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                <div class="text-subtitle-1 font-weight-bold mb-3">SimpliGPS Driver sign-in</div>

                <template v-if="credential">
                    <div class="d-flex align-center ga-2 mb-3">
                        <v-chip :color="access.color" variant="tonal" :prepend-icon="access.icon">
                            {{ access.label }}
                        </v-chip>
                    </div>
                    <div class="text-body-2 text-medium-emphasis mb-3">{{ access.help }}</div>

                    <div class="info-row">
                        <v-icon size="18">mdi-cellphone-key</v-icon>
                        <span class="info-label">Phone number</span>
                        <span class="font-weight-medium">{{ credential.phone || '--' }}</span>
                    </div>
                    <div class="info-row">
                        <v-icon size="18">mdi-login</v-icon>
                        <span class="info-label">Last sign-in</span>
                        <span class="font-weight-medium">{{ credential.last_login_at ? fmtDateTime(credential.last_login_at) : 'Never' }}</span>
                    </div>
                    <div class="info-row">
                        <v-icon size="18">mdi-email-fast-outline</v-icon>
                        <span class="info-label">First invited</span>
                        <span class="font-weight-medium">{{ credential.first_invited_at ? fmtDateTime(credential.first_invited_at) : '--' }}</span>
                    </div>

                    <div class="d-flex flex-wrap ga-2 mt-4">
                        <v-btn v-if="credential.status !== 'active' || !credential.activated" size="small"
                               color="primary" variant="tonal" prepend-icon="mdi-cellphone-message"
                               :loading="inviting" @click="$emit('invite')">
                            {{ credential.status === 'active' ? 'Resend invite' : 'Invite again' }}
                        </v-btn>
                        <v-btn v-if="isSuperAdmin && credential.status === 'active'" size="small" color="error"
                               variant="text" prepend-icon="mdi-phone-off" @click="releaseDialog = true">
                            Release phone number
                        </v-btn>
                    </div>
                </template>

                <template v-else>
                    <div class="text-body-2 text-medium-emphasis mb-4">
                        This driver has not been invited to the SimpliGPS Driver app yet. The invite is a text
                        message to their phone with a link to the app.
                    </div>
                    <v-btn color="primary" variant="flat" prepend-icon="mdi-cellphone-message" :loading="inviting"
                           @click="$emit('invite')">Invite to app</v-btn>
                </template>
            </v-card>
        </v-col>

        <!-- Installs -->
        <v-col cols="12" md="7">
            <v-card rounded="lg" variant="outlined" class="pa-4 h-100">
                <div class="text-subtitle-1 font-weight-bold mb-3">Phones with the app</div>

                <div v-if="!installs.length" class="text-body-2 text-medium-emphasis py-4">
                    The app has not reported in from any phone yet. Phones appear here after the driver
                    opens the app and signs in.
                </div>

                <div v-else class="d-flex flex-column ga-3">
                    <div v-for="i in installs" :key="i.id" class="install-row">
                        <v-avatar size="40" :color="i.platform === 'ios' ? 'grey-darken-3' : 'green-darken-1'">
                            <v-icon color="white">{{ i.platform === 'ios' ? 'mdi-apple' : 'mdi-android' }}</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1 min-w-0">
                            <div class="font-weight-medium text-truncate">
                                {{ i.device_model || (i.platform === 'ios' ? 'iPhone' : 'Android phone') }}
                            </div>
                            <div class="text-caption text-medium-emphasis">
                                {{ i.platform === 'ios' ? 'iOS' : 'Android' }} {{ i.os_version || '' }}
                                &middot; App {{ i.app_version || '?' }}<span v-if="i.app_build"> ({{ i.app_build }})</span>
                            </div>
                        </div>
                        <div class="text-right text-no-wrap">
                            <v-chip size="x-small" :color="seenColor(i.last_seen_at)" variant="tonal">
                                {{ seenAgo(i.last_seen_at) }}
                            </v-chip>
                            <div class="text-caption text-medium-emphasis mt-1">
                                Installed {{ i.installed_at ? fmtDate(i.installed_at) : (i.first_seen_at ? fmtDate(i.first_seen_at) : '--') }}
                            </div>
                        </div>
                    </div>
                </div>
            </v-card>
        </v-col>
    </v-row>

    <!-- Support only: free the number when a stale claim blocks someone else -->
    <v-dialog v-model="releaseDialog" max-width="460">
        <v-card class="pa-2">
            <v-card-title class="text-subtitle-1 font-weight-bold">Release this phone number?</v-card-title>
            <v-card-text class="text-body-2">
                The driver is signed out of the app on every phone and their PIN is removed. The number
                {{ credential?.phone }} is then free for someone else. To give this driver access again,
                send a new invite.
            </v-card-text>
            <v-card-actions>
                <v-spacer/>
                <v-btn variant="text" :disabled="releasing" @click="releaseDialog = false">Cancel</v-btn>
                <v-btn color="error" variant="flat" :loading="releasing" @click="release">Release number</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";
import {formatCompanyDateTime} from "@/composables/useTimezone";

const props = defineProps({
    driverId: {type: [Number, String], required: true},
    inviting: {type: Boolean, default: false},
});
defineEmits(["invite"]);

const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();
const isSuperAdmin = computed(() => !!authStore.user?.super);

const credential = ref(null);
const installs = ref([]);
const loading = ref(false);
const loaded = ref(false);
const loadError = ref("");
const releaseDialog = ref(false);
const releasing = ref(false);

// The company timezone, as the rest of the portal shows device times.
const fmtDateTime = (ts) => formatCompanyDateTime(ts, {second: undefined});
const fmtDate = (ts) => formatCompanyDateTime(ts, {hour: undefined, minute: undefined, second: undefined});

const access = computed(() => {
    const c = credential.value;
    if (!c) return {};
    if (c.status !== "active") {
        return {label: "Access removed", color: "grey", icon: "mdi-account-cancel",
            help: "This driver cannot sign in. Invite them again to give access back."};
    }
    if (!c.activated) {
        return {label: "Invited, not set up", color: "warning", icon: "mdi-timer-sand",
            help: "The driver has the invite but has not set a PIN yet."};
    }
    return {label: "Active", color: "success", icon: "mdi-check-circle",
        help: "The driver can sign in with this phone number and their PIN."};
});

const hoursSince = (ts) => (Date.now() - Date.parse(ts)) / 3600000;

const seenAgo = (ts) => {
    if (!ts) return "Never seen";
    const h = hoursSince(ts);
    if (h < 1) return "Seen in the last hour";
    if (h < 24) return `Seen ${Math.floor(h)}h ago`;
    const d = Math.floor(h / 24);
    return d === 1 ? "Seen yesterday" : `Seen ${d} days ago`;
};

const seenColor = (ts) => {
    if (!ts) return "grey";
    const h = hoursSince(ts);
    if (h < 24) return "success";
    if (h < 24 * 7) return "warning";
    return "grey";
};

const load = () => {
    loading.value = true;
    loadError.value = "";
    send("get", `companies/drivers/${props.driverId}/mobile-apps`)
        .then((r) => {
            credential.value = r.data.data?.credential ?? null;
            installs.value = r.data.data?.installs ?? [];
            loaded.value = true;
        })
        .catch((e) => {
            loadError.value = e?.response?.data?.message || "Mobile apps could not be loaded.";
        })
        .finally(() => {
            loading.value = false;
        });
};

const release = () => {
    if (!credential.value) return;
    releasing.value = true;
    send("post", `admin/driver-app-credentials/${credential.value.id}/revoke`)
        .then((r) => {
            showSnackbar(r.data?.message || "Phone number released");
            releaseDialog.value = false;
            load();
        })
        .catch((e) => showSnackbar(e?.response?.data?.message || "Could not release the number", "error"))
        .finally(() => {
            releasing.value = false;
        });
};

watch(() => props.driverId, load);
onMounted(load);

defineExpose({refresh: load});
</script>

<style scoped>
.h-100 {
    height: 100%;
}

.info-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
}

.info-label {
    min-width: 110px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    font-size: 0.875rem;
}

.install-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(var(--v-theme-on-surface), 0.04);
}

.min-w-0 {
    min-width: 0;
}
</style>
